import React from "react";
import "@/App.css";
import "./dark.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { FilterProvider } from "./lib/FilterContext";
import { ThemeProvider } from "./lib/ThemeContext";
import { MobileFrame } from "./components/MobileFrame";
import { BottomNav } from "./components/BottomNav";

import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Filters from "./pages/Filters";
import Profile from "./pages/Profile";
import AddContainer from "./pages/AddContainer";
import ContainerDetail from "./pages/ContainerDetail";
import LockedContainer from "./pages/LockedContainer";
import NotificationSettings from "./pages/NotificationSettings";
import ScanLanding from "./pages/ScanLanding";
import ContainerSaved from "./pages/ContainerSaved";
import Settings from "./pages/Settings";
import HelpSupport from "./pages/HelpSupport";
import Household from "./pages/Household";
import PrintStickers from "./pages/PrintStickers";
import QRLabels from "./pages/QRLabels";
import BillingSuccess from "./pages/BillingSuccess";
import AdminHealth from "./pages/AdminHealth";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-[#4F5D75]">Loading…</div>
);

const Gate = ({ children, requireHousehold = true }) => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/auth" replace />;
  if (requireHousehold && !user.household_id) return <Navigate to="/onboarding" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) {
    if (!user.household_id) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
};

const Shell = ({ children, showNav = true }) => (
  <MobileFrame>
    {children}
    {showNav && <BottomNav />}
  </MobileFrame>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <FilterProvider>
            <Routes>
              <Route path="/auth" element={<PublicOnly><MobileFrame><Auth /></MobileFrame></PublicOnly>} />
              <Route path="/forgot-password" element={
                <PublicOnly><MobileFrame><ForgotPassword /></MobileFrame></PublicOnly>
              } />
              {/* /reset-password is reachable even if a stale session exists —
                  user may have ended up here from an email link without realising
                  they're already signed in. AuthContext handles re-auth on success. */}
              <Route path="/reset-password" element={<MobileFrame><ResetPassword /></MobileFrame>} />
              <Route path="/onboarding" element={
                <Gate requireHousehold={false}>
                  <MobileFrame><Onboarding /></MobileFrame>
                </Gate>
              } />

              <Route path="/" element={<Gate><Shell><Dashboard /></Shell></Gate>} />
              <Route path="/inventory" element={<Gate><Shell><Inventory /></Shell></Gate>} />
              <Route path="/filters" element={<Gate><Shell><Filters /></Shell></Gate>} />
              <Route path="/profile" element={<Gate><Shell><Profile /></Shell></Gate>} />
              <Route path="/add" element={<Gate><Shell><AddContainer /></Shell></Gate>} />
              <Route path="/container/:id" element={<Gate><Shell><ContainerDetail /></Shell></Gate>} />
              <Route path="/locked/:qr" element={<Gate><Shell><LockedContainer /></Shell></Gate>} />
              <Route path="/c/:code" element={<Gate><Shell><ScanLanding /></Shell></Gate>} />
              <Route path="/notifications" element={<Gate><Shell><NotificationSettings /></Shell></Gate>} />
              <Route path="/settings" element={<Gate><Shell><Settings /></Shell></Gate>} />
              <Route path="/help" element={<Gate><Shell><HelpSupport /></Shell></Gate>} />
              <Route path="/household" element={<Gate><Shell><Household /></Shell></Gate>} />
              <Route path="/print-stickers" element={<Gate><Shell><PrintStickers /></Shell></Gate>} />
              <Route path="/qr-labels" element={<Gate><Shell><QRLabels /></Shell></Gate>} />
              <Route path="/saved/:id" element={<Gate><Shell><ContainerSaved /></Shell></Gate>} />
              <Route path="/billing/success" element={<Gate><Shell><BillingSuccess /></Shell></Gate>} />
              <Route path="/admin/health" element={<Gate><Shell><AdminHealth /></Shell></Gate>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  fontFamily: "'Manrope', sans-serif",
                },
                className: "qfresh-toast",
              }}
            />
            </FilterProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
